import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/* Integration data                                                     */
/* ------------------------------------------------------------------ */

interface Integration {
  href: string
  title: string
  portal: string
  description: string
}

const INTEGRATIONS: Integration[] = [
  {
    href: "/integrations/rightmove",
    title: "Rightmove video ads",
    portal: "Rightmove",
    description:
      "Paste any Rightmove listing URL and create a branded property video ad in 60 seconds. Photos, price, and features imported automatically.",
  },
  {
    href: "/integrations/zoopla",
    title: "Zoopla video ads",
    portal: "Zoopla",
    description:
      "Create branded video ads from any Zoopla listing in 60 seconds. Works with both sales and lettings listings.",
  },
  {
    href: "/integrations/onthemarket",
    title: "OnTheMarket video ads",
    portal: "OnTheMarket",
    description:
      "Paste any OnTheMarket URL and get a professional property video ad. All three UK portals supported by QuiteRight.",
  },
]

const HOW_IT_WORKS = [
  {
    number: "1",
    title: "Copy the listing URL",
    description:
      "Find the property on Rightmove, Zoopla, or OnTheMarket. Copy the URL from your browser.",
  },
  {
    number: "2",
    title: "QuiteRight imports everything",
    description:
      "Photos, price, address, key features, number of bedrooms, and property type. All pulled automatically from the listing page.",
  },
  {
    number: "3",
    title: "Pick a template and publish",
    description:
      "Choose a template. QuiteRight generates the script, voiceover, and music. Your branded video is ready in 60 seconds.",
  },
]

/* ------------------------------------------------------------------ */
/* Integration card                                                     */
/* ------------------------------------------------------------------ */

function IntegrationCard({ integration }: { integration: Integration }) {
  return (
    <Link
      href={integration.href}
      className="group flex flex-col rounded-xl border border-stone bg-white p-6 transition-all hover:border-red/30 hover:shadow-md hover:shadow-red/5"
    >
      <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-warm-grey">
        Portal integration
      </p>
      <h3 className="mt-3 font-display text-lg font-bold text-charcoal transition-colors group-hover:text-red">
        {integration.title}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate">
        {integration.description}
      </p>
      <p className="mt-4 flex items-center gap-1.5 text-sm font-medium text-red">
        Learn more
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className="size-4 transition-transform group-hover:translate-x-1"
        >
          <path
            fillRule="evenodd"
            d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
            clipRule="evenodd"
          />
        </svg>
      </p>
    </Link>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                       */
/* ------------------------------------------------------------------ */

export default function IntegrationsIndexPage({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Portal Integrations",
            description: meta.description,
            numberOfItems: INTEGRATIONS.length,
            itemListElement: INTEGRATIONS.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.title,
              description: item.description,
              url: `https://quiteright.co.uk${item.href}`,
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Integrations" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Portal integrations
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Paste a listing, get a video
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            QuiteRight connects to all three major UK property portals.
            Import listing photos, prices, and features automatically, then
            create a branded video ad in 60 seconds.
          </p>
        </div>
      </section>

      {/* Integration cards */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Supported portals
              <span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              Create video ads from listings on any of these property portals.
            </p>
          </ScrollReveal>

          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {INTEGRATIONS.map((integration) => (
              <ScrollReveal key={integration.href}>
                <IntegrationCard integration={integration} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              How it works
              <span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              The same simple process, whichever portal you use.
            </p>
          </ScrollReveal>

          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {HOW_IT_WORKS.map((step) => (
              <ScrollReveal key={step.number}>
                <div className="flex gap-4">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red font-display text-sm font-bold text-white">
                    {step.number}
                  </span>
                  <div>
                    <p className="font-display text-base font-bold text-charcoal">
                      {step.title}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-slate">
                      {step.description}
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* What gets imported */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              What gets imported
              <span className="text-red">.</span>
            </h2>
            <p className="mt-2 text-sm text-slate">
              QuiteRight pulls everything it needs from your listing page automatically.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { title: "Listing photos", desc: "All property images, used as video scenes in your chosen template." },
              { title: "Price", desc: "Sale price or rent displayed on screen and mentioned in the voiceover." },
              { title: "Address", desc: "Full address and postcode for location context." },
              { title: "Key features", desc: "Bullet-point features from the listing, woven into the script." },
              { title: "Bedrooms and bathrooms", desc: "Room counts displayed as headline stats in the video." },
              { title: "Property type", desc: "Detached, semi, flat, bungalow. Used to tailor the voiceover script." },
            ].map((item) => (
              <div key={item.title} className="rounded-xl border border-stone bg-white p-5">
                <p className="font-display text-sm font-bold text-charcoal">{item.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate">{item.desc}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* More resources */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              More resources
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/guides",
                title: "Guides",
                desc: "Free guides for estate agents and home sellers.",
              },
              {
                href: "/tools",
                title: "Tools & calculators",
                desc: "Free property calculators for agents and buyers.",
              },
              {
                href: "/how-it-works",
                title: "How it works",
                desc: "See the full video creation process step by step.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Your listings, already waiting
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Every listing you have on Rightmove, Zoopla, or OnTheMarket is
            ready to become a scroll-stopping video ad. Paste the URL and
            QuiteRight does the rest.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
